import React, { useState, useEffect } from 'react';
import { accountService } from '../../../services/accountService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AddDebtModal = ({ onClose, onAdd }) => {
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [formData, setFormData] = useState({
    debtName: '',
    linkedAccountId: '',
    principalAmount: '',
    currentBalance: '',
    interestRate: '',
    minimumPayment: '',
    additionalFees: '0',
    insuranceFee: '0',
    payoffStrategy: 'avalanche',
    paymentFrequency: 'monthly',
    targetPayoffDate: '',
    startDate: '',
    endDate: '',
    termLength: ''
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const allAccounts = await accountService?.getAll();
      const liabilityAccounts = allAccounts?.filter(acc => acc?.group === 'Liabilities');
      setAccounts(liabilityAccounts);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.debtName?.trim()) {
      setError('Please enter a debt name');
      return;
    }

    if (!formData?.principalAmount || parseFloat(formData?.principalAmount) <= 0) {
      setError('Please enter a valid principal amount');
      return;
    }

    // Allow zero balance if account is linked (will auto-populate from account)
    if (!formData?.linkedAccountId && (!formData?.currentBalance || parseFloat(formData?.currentBalance) <= 0)) {
      setError('Please enter a valid current balance or link an account');
      return;
    }

    if (!formData?.interestRate || parseFloat(formData?.interestRate) < 0) {
      setError('Please enter a valid interest rate');
      return;
    }

    if (!formData?.minimumPayment || parseFloat(formData?.minimumPayment) <= 0) {
      setError('Please enter a valid minimum payment');
      return;
    }

    try {
      await onAdd({
        debtName: formData?.debtName,
        linkedAccountId: formData?.linkedAccountId || null,
        principalAmount: parseFloat(formData?.principalAmount),
        currentBalance: parseFloat(formData?.currentBalance),
        interestRate: parseFloat(formData?.interestRate),
        minimumPayment: parseFloat(formData?.minimumPayment),
        additionalFees: parseFloat(formData?.additionalFees) || 0,
        insuranceFee: parseFloat(formData?.insuranceFee) || 0,
        payoffStrategy: formData?.payoffStrategy,
        paymentFrequency: formData?.paymentFrequency,
        targetPayoffDate: formData?.targetPayoffDate || null,
        startDate: formData?.startDate || null,
        endDate: formData?.endDate || null,
        termLength: formData?.termLength ? parseInt(formData?.termLength) : null
      });
    } catch (err) {
      setError(err?.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-24 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl font-semibold text-card-foreground">Add New Debt</h2>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-24">
          {error && (
            <div className="mb-16 p-12 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-8">
                Debt Name *
              </label>
              <input
                type="text"
                value={formData?.debtName}
                onChange={(e) => setFormData({ ...formData, debtName: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="e.g., Credit Card, Personal Loan"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-8">
                Link to Liability Account (Optional)
              </label>
              <select
                value={formData?.linkedAccountId}
                onChange={(e) => setFormData({ ...formData, linkedAccountId: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="">No linked account</option>
                {accounts?.map(account => (
                  <option key={account?.id} value={account?.id}>
                    {account?.name} ({account?.type})
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-6">
                Link to Credit Card, Loan, Line of Credit, or Collections account
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Original Principal Amount *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.principalAmount}
                onChange={(e) => setFormData({ ...formData, principalAmount: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Current Balance *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.currentBalance}
                onChange={(e) => setFormData({ ...formData, currentBalance: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
              {formData?.linkedAccountId && (
                <p className="text-xs text-muted-foreground mt-6">
                  <Icon name="Info" size={12} className="inline mr-4" />
                  Will auto-populate from linked account balance if left at 0
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Interest Rate (APR) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.interestRate}
                onChange={(e) => setFormData({ ...formData, interestRate: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Minimum Payment *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.minimumPayment}
                onChange={(e) => setFormData({ ...formData, minimumPayment: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Payment Frequency *
              </label>
              <select
                value={formData?.paymentFrequency}
                onChange={(e) => setFormData({ ...formData, paymentFrequency: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly (Every 2 weeks)</option>
                <option value="semi_monthly">Semi-monthly (Twice a month)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Additional Fees (Monthly)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.additionalFees}
                onChange={(e) => setFormData({ ...formData, additionalFees: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Insurance Fee (Monthly)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.insuranceFee}
                onChange={(e) => setFormData({ ...formData, insuranceFee: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Payoff Strategy
              </label>
              <select
                value={formData?.payoffStrategy}
                onChange={(e) => setFormData({ ...formData, payoffStrategy: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="avalanche">Avalanche (Highest Interest First)</option>
                <option value="snowball">Snowball (Smallest Balance First)</option>
                <option value="hybrid">Hybrid</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Target Payoff Date (Optional)
              </label>
              <input
                type="date"
                value={formData?.targetPayoffDate}
                onChange={(e) => setFormData({ ...formData, targetPayoffDate: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Start Date (Optional)
              </label>
              <input
                type="date"
                value={formData?.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
              <p className="text-xs text-muted-foreground mt-6">
                Original start date of this debt
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                End Date (Optional)
              </label>
              <input
                type="date"
                value={formData?.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
              <p className="text-xs text-muted-foreground mt-6">
                Original maturity/end date of this debt
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Term Length (Months) (Optional)
              </label>
              <input
                type="number"
                value={formData?.termLength}
                onChange={(e) => setFormData({ ...formData, termLength: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="e.g., 60 for 5-year loan"
              />
              <p className="text-xs text-muted-foreground mt-6">
                Original term length in months
              </p>
            </div>
          </div>

          <div className="flex gap-12 mt-24">
            <Button type="button" onClick={onClose} className="flex-1 bg-muted text-foreground hover:bg-muted/80">
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              Add Debt
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDebtModal;